import type { ReactNode } from 'react'
import { motion } from 'motion/react'
import type { LucideIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { CollapsePresence, useCollapseFade } from '@/components/shared/animated-collapse'

export interface CardSummaryItem {
  key: string
  icon: LucideIcon
  label: string
  /** Screen-reader text read after the label, e.g. "guests" after a bare count. */
  srLabel?: string
  /**
   * Lets this item shrink and truncate when the header runs out of room. Give
   * it to one item at most (the city); the rest keep their full text.
   */
  truncate?: boolean
}

interface CardSummaryItemsProps {
  /** Shown while true; fades in after the card body's collapse has run, and out at once. */
  show: boolean
  /** The list's accessible name, e.g. "Inquiry summary". */
  label: string
  items: CardSummaryItem[]
  /** Extra `<li>` elements after the items (e.g. a badge). */
  children?: ReactNode
}

/**
 * A collapsed card's one-line summary: compact icon + text items that sit in
 * the header beside the title. Render it inside the title row so the
 * collapsed card stays a single row.
 *
 * The title row aligns on baselines, so the list and its items do too, with
 * each icon centred on its own (`self-center`, which keeps it out of baseline
 * alignment). Centring the items instead would leave the list no text
 * baseline, and the browser would fall back to the first icon's bottom edge,
 * lifting the whole summary above the title.
 */
export function CardSummaryItems({ show, label, items, children }: CardSummaryItemsProps) {
  // Collapsing, the header's other swapping pieces leave first, then the
  // summary fades in; expanding runs the other way round.
  const summaryFade = useCollapseFade({ afterExit: true })

  return (
    <CollapsePresence>
      {show && (
        <motion.ul
          key="card-summary"
          {...summaryFade}
          aria-label={label}
          className="flex min-w-0 items-baseline gap-x-3 overflow-hidden text-sm text-muted-foreground"
        >
          {items.map(({ key, icon: Icon, label: text, srLabel, truncate }) => (
            <li
              key={key}
              className={cn('inline-flex items-baseline gap-1 whitespace-nowrap', truncate ? 'min-w-0' : 'shrink-0')}
            >
              <Icon aria-hidden className="h-3.5 w-3.5 shrink-0 self-center" />
              <span className="truncate">{text}</span>
              {srLabel && <span className="sr-only"> {srLabel}</span>}
            </li>
          ))}
          {children}
        </motion.ul>
      )}
    </CollapsePresence>
  )
}
